/**
 * Grafana trace URL generator utility
 * Generates Grafana Explore URLs for trace visualization
 */

import { getConfig } from '@/config/runtime-config';

export interface GrafanaConfig {
  baseUrl: string;
  datasourceUid: string;
  orgId: string;
}

const GRAFANA_NOT_CONFIGURED_WARNING =
  'Grafana trace viewer is not configured. Set grafanaBaseUrl, grafanaDatasourceUid, and grafanaOrgId to enable trace links.';

const normalizeConfigValue = (value?: string): string => value?.trim() || '';

const getRuntimeGrafanaConfig = (): Partial<GrafanaConfig> => {
  try {
    const config = getConfig();

    return {
      baseUrl: normalizeConfigValue(config.grafanaBaseUrl),
      datasourceUid: normalizeConfigValue(config.grafanaDatasourceUid),
      orgId: normalizeConfigValue(config.grafanaOrgId)
    };
  } catch {
    return {};
  }
};

const resolveGrafanaConfig = (config: Partial<GrafanaConfig> = {}): GrafanaConfig | null => {
  const runtimeConfig = getRuntimeGrafanaConfig();
  const grafanaConfig = {
    baseUrl: normalizeConfigValue(config.baseUrl) || normalizeConfigValue(runtimeConfig.baseUrl),
    datasourceUid:
      normalizeConfigValue(config.datasourceUid) || normalizeConfigValue(runtimeConfig.datasourceUid),
    orgId: normalizeConfigValue(config.orgId) || normalizeConfigValue(runtimeConfig.orgId)
  };

  if (!grafanaConfig.baseUrl || !grafanaConfig.datasourceUid || !grafanaConfig.orgId) {
    return null;
  }

  return grafanaConfig;
};

export const isGrafanaTraceConfigured = (config: Partial<GrafanaConfig> = {}): boolean =>
  resolveGrafanaConfig(config) !== null;

/**
 * Generates a Grafana Explore URL for viewing a specific trace
 * @param traceId - The trace ID to search for
 * @param config - Optional Grafana configuration override
 * @param timeRange - Optional time range (defaults to last hour)
 * @returns Complete Grafana URL for the trace
 */
export const generateGrafanaTraceUrl = (
  traceId: string,
  config: Partial<GrafanaConfig> = {},
  timeRange: { from: string; to: string } = { from: 'now-1h', to: 'now' }
): string => {
  if (!traceId || traceId.trim() === '') {
    throw new Error('Trace ID is required');
  }

  const grafanaConfig = resolveGrafanaConfig(config);

  if (!grafanaConfig) {
    throw new Error('Grafana trace configuration is required');
  }

  const panes = {
    "olp": {
      "datasource": grafanaConfig.datasourceUid,
      "queries": [{
        "refId": "A",
        "datasource": {
          "type": "tempo",
          "uid": grafanaConfig.datasourceUid
        },
        "queryType": "traceql",
        "limit": 20,
        "tableType": "traces",
        "metricsQueryType": "range",
        "query": traceId.trim(),
        "filters": [{
          "id": "4708bff3",
          "operator": "=",
          "scope": "span"
        }]
      }],
      "range": timeRange,
      "panelsState": {
        "trace": {
          "spanFilters": {
            "spanNameOperator": "=",
            "serviceNameOperator": "=",
            "fromOperator": ">",
            "toOperator": "<",
            "tags": [{
              "id": "484355c5-24b",
              "operator": "="
            }]
          }
        }
      },
      "compact": false
    }
  };

  const params = new URLSearchParams({
    schemaVersion: '1',
    panes: JSON.stringify(panes),
    orgId: grafanaConfig.orgId
  });

  return `${grafanaConfig.baseUrl}?${params.toString()}`;
};

/**
 * Opens a Grafana trace URL in a new tab/window
 * @param traceId - The trace ID to search for
 * @param config - Optional Grafana configuration
 * @param timeRange - Optional time range
 */
export const openGrafanaTrace = (
  traceparent: string,
  config?: Partial<GrafanaConfig>,
  timeRange?: { from: string; to: string }
): void => {
  if (!isGrafanaTraceConfigured(config)) {
    console.warn(GRAFANA_NOT_CONFIGURED_WARNING);
    return;
  }

  try {
    const traceId = extractTraceId(traceparent);
    const url = generateGrafanaTraceUrl(traceId, config, timeRange);
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Failed to open Grafana trace:', error);
  }
};

/**
 * Extracts the trace ID from W3C Trace Context format
 * Format: 00-{trace-id}-{span-id}-{flags}
 * Example: 00-b852f31f1600ee21473c96f0eddddef7-774a36ebe02a4ff1-01
 * Returns: b852f31f1600ee21473c96f0eddddef7
 * @param traceparent - The full traceparent string
 * @returns The extracted trace ID or the original string if not in W3C format
 */
export const extractTraceId = (traceparent: string): string => {
  if (!traceparent || typeof traceparent !== 'string') {
    return traceparent || '';
  }

  const cleanTraceparent = traceparent.trim();
  
  // Check if it matches W3C Trace Context format: 00-{trace-id}-{span-id}-{flags}
  const w3cPattern = /^[0-9a-f]{2}-([0-9a-f]{32})-[0-9a-f]{16}-[0-9a-f]{2}$/i;
  const match = cleanTraceparent.match(w3cPattern);
  
  if (match && match[1]) {
    // Return the trace ID part (second segment)
    return match[1];
  }
  
  // If not in W3C format, return the original string
  return cleanTraceparent;
};

/**
 * Validates if a string looks like a valid trace ID
 * @param traceId - The trace ID to validate
 * @returns true if the trace ID appears to be valid
 */
export const isValidTraceId = (traceparent: string): boolean => {
  if (!traceparent || typeof traceparent !== 'string') {
    return false;
  }

  const cleanTraceparent = traceparent.trim();
  
  // If it's empty after trimming, it's not valid
  if (cleanTraceparent.length === 0) {
    return false;
  }
  
  // Check if it's in W3C Trace Context format
  const w3cPattern = /^[0-9a-f]{2}-[0-9a-f]{32}-[0-9a-f]{16}-[0-9a-f]{2}$/i;
  if (w3cPattern.test(cleanTraceparent)) {
    return true;
  }
  
  // Check if it's a plain hex trace ID (32 characters)
  const hexPattern = /^[0-9a-f]{32}$/i;
  if (hexPattern.test(cleanTraceparent)) {
    return true;
  }
  
  // For other formats, be more lenient but require reasonable length
  return cleanTraceparent.length >= 16;
};

/**
 * Creates a formatted trace display with click handler
 * @param traceId - The trace ID to display
 * @param onClick - Optional custom click handler
 * @returns Object with display properties and click handler
 */
export const createTraceDisplayProps = (
  traceparent: string,
  onClick?: (traceparent: string) => void
) => {
  const normalizedTraceparent = typeof traceparent === 'string' ? traceparent.trim() : '';
  const hasTrace = normalizedTraceparent !== '' && normalizedTraceparent !== '-';
  const traceConfigured = isGrafanaTraceConfigured();
  const isValid = hasTrace && isValidTraceId(traceparent);
  const isClickable = onClick ? isValid : isValid && traceConfigured;

  const handleClick = isClickable
    ? () => {
        if (onClick) {
          onClick(traceparent);
        } else {
          openGrafanaTrace(traceparent);
        }
      }
    : undefined;

  const extractedTraceId = hasTrace ? extractTraceId(traceparent) : '';
  let title = `View trace ${extractedTraceId} in Grafana`;

  if (!hasTrace) {
    title = 'Trace ID is not available';
  } else if (!isValid) {
    title = 'Trace ID is invalid';
  } else if (!onClick && !traceConfigured) {
    title = 'Grafana trace viewer is not configured';
  }
  
  return {
    traceparent,
    extractedTraceId,
    isValid,
    displayText: hasTrace ? traceparent : 'N/A',
    onClick: handleClick,
    title,
    style: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: isClickable ? '#1976d2' : '#666',
      textDecoration: isClickable ? 'underline' : 'none',
      cursor: isClickable ? 'pointer' : 'default'
    }
  };
};

/**
 * Predefined time ranges for Grafana queries
 */
export const GRAFANA_TIME_RANGES = {
  LAST_15_MINUTES: { from: 'now-15m', to: 'now' },
  LAST_HOUR: { from: 'now-1h', to: 'now' },
  LAST_6_HOURS: { from: 'now-6h', to: 'now' },
  LAST_24_HOURS: { from: 'now-24h', to: 'now' },
  LAST_7_DAYS: { from: 'now-7d', to: 'now' }
} as const;